import type {Draft} from "@reduxjs/toolkit";
import type {FetchBaseQueryError} from "@reduxjs/toolkit/query";
import type {DriverActivity} from "../../model/driver-activity";
import type {ObjectCostRateModel} from "../../model/object-cost-rate-model";
import {ShiftCostRequest, ShiftCostResponse, ShiftCostResponseValidator} from "../../model/shift-cost";
import type {TransportAssoc} from "../../model/transport-assoc";
import {driverActivityApi, driverActivityCacheUpdate} from "../entity/driver-activity/driver-activity-store";
import {otsBaseApi} from "../ots-api";

// The nested parts of a refresh merge into the ticket's own; the ticket codes are not shown in the day.
const NESTED_REFRESH = new Set(["pickupTransport", "deliveryTransport", "ticketCodeList"]);

/**
 * Puts a costing's changes into the day's rows: each shift's new revenue and cost, and each ticket's cost lines,
 * billable time, refreshed fields, and warnings. A ticket from another tenant only changes its own rows.
 */
export const applyShiftCostToDriverActivity = (rows: DriverActivity[] | Draft<DriverActivity[]>, response: ShiftCostResponse) => {
  for (const activity of response.activityList ?? []) {
    const shift = activity.startShift;
    if (shift) {
      for (const row of rows) {
        for (const side of [row.startShift, row.endShift]) {
          if (side?.id !== shift.id) continue;
          side.revenue = shift.revenue ?? undefined;
          side.cost = shift.cost ?? undefined;
          if (shift.lastModifiedDttm != null) side.lastModifiedDttm = shift.lastModifiedDttm;
        }
      }
    }
    const ticket = activity.transportTransportAssoc;
    if (!ticket) continue;
    for (const row of rows) {
      const tta = row.transportTransportAssoc as TransportAssoc | undefined;
      if (tta?.id !== ticket.id || (ticket.database && tta.database && tta.database !== ticket.database)) continue;
      const refresh = ticket.refresh ?? {};
      Object.assign(tta, Object.fromEntries(Object.entries(refresh).filter(([key, value]) => value != null && !NESTED_REFRESH.has(key))));
      for (const side of ["pickupTransport", "deliveryTransport"] as const) {
        const nested = refresh[side];
        if (tta[side] && nested && typeof nested === "object") Object.assign(tta[side] as object, nested);
      }
      tta.objectCostRateModelList = (ticket.objectCostRateModelList ?? []) as ObjectCostRateModel[];
      if (ticket.billableTime != null) tta.billableTime = ticket.billableTime;
      if (activity.warnings) row.warnings = activity.warnings;
    }
  }
};

export const shiftCostApi = otsBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Costs a shift's tickets (`POST /reports/shift/{id}/cost`). The changes join the cached day as they come
     * back, so it is not fetched again; a preview's figures show there too, unsaved.
     */
    costShift: builder.mutation<ShiftCostResponse, ShiftCostRequest>({
      query: ({shiftId, rateModelIds, preview, progressId}) => ({
        url: `/reports/shift/${encodeURIComponent(shiftId)}/cost`,
        method: "POST",
        params: {
          ...(rateModelIds?.length ? {rateModelIds: rateModelIds.join(",")} : {}),
          ...(preview ? {preview: true} : {}),
          ...(progressId ? {progressId} : {}),
        },
      }),
      transformResponse: (value: unknown) => {
        const decoded = ShiftCostResponseValidator.decode(value);
        if (decoded._tag === "Right") return decoded.right;
        throw new Error("The shift cost response could not be read.");
      },
      transformErrorResponse: (error: FetchBaseQueryError) => error,
      onQueryStarted: async (_request, {dispatch, getState, queryFulfilled}) => {
        try {
          const {data} = await queryFulfilled;
          driverActivityCacheUpdate(dispatch, getState(), (rows) => applyShiftCostToDriverActivity(rows, data));
          // Filtered searches may hold tickets the day does not.
          dispatch(driverActivityApi.util.invalidateTags([{type: "DriverActivity", id: "LIST"}]));
        } catch {
          // The caller reports the failure.
        }
      },
    }),
    /** A running job's stages (`GET /progress/{id}/details`); missing until the job starts. */
    getProgressDetails: builder.query<unknown, string>({
      query: (progressId) => ({url: `/progress/${encodeURIComponent(progressId)}/details`, method: "GET"}),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {useCostShiftMutation, useGetProgressDetailsQuery} = shiftCostApi;
